<template>
  <div>
    <b-modal
      :id="MODAL_ID"
      :title="modalTitle"
      size="xl"
      scrollable
      dialog-class="make-editable-modal-dialog"
      body-class="make-editable-modal-body"
      :hide-footer="currentStep !== 2 && !isLoading"
      footer-class="make-editable-progress-footer"
      no-close-on-backdrop
      :no-close-on-esc="isAnalyzingRegion"
      :hide-header-close="isAnalyzingRegion"
      @show="onModalOpen"
      @hide="onModalHide"
    >
      <!-- Step 1: Initial Selection -->
      <div
        v-if="currentStep === 1"
        class="step-one-content"
      >
        <div class="form-group mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label
              class="mb-0"
              for="backgroundImageSelectV2"
            >Select Image(s) ({{ selectedTemplates.length }} selected)</label>
            <div>
              <a
                href="#"
                class="text-secondary small mr-2"
                @click.prevent="selectAllFirstImages"
              >Select all</a>
              <a
                href="#"
                class="text-secondary small"
                @click.prevent="selectedTemplates = []"
              >Clear</a>
            </div>
          </div>
          <Multiselect
            id="backgroundImageSelectV2"
            v-model="selectedTemplates"
            class="image-select-dropdown"
            :options="groupedBackgroundImages"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :group-select="true"
            group-label="instanceId"
            group-values="images"
            placeholder="Select images"
            label="text"
            track-by="value"
          >
            <template
              slot="option"
              slot-scope="props"
            >
              <div
                v-if="props.option.$isLabel"
                class="template-group-label"
              >
                {{ getTemplateGroupName(props.option.$groupLabel) }}
              </div>
              <div
                v-else
                class="d-flex align-items-center"
              >
                <img
                  v-if="props.option.thumbnail"
                  :src="props.option.thumbnail"
                  class="image-thumbnail mr-2"
                  alt=""
                >
                <span
                  v-else
                  class="image-thumbnail-placeholder mr-2"
                />
                <span>{{ props.option.text }}</span>
              </div>
            </template>
          </Multiselect>
        </div>

        <div
          class="selected-images-preview mb-4 d-flex justify-content-start align-items-center pb-3"
        >
          <div 
            v-for="template in selectedTemplates" 
            :key="template.value" 
            class="d-inline-flex flex-column align-items-center"
          >
            <img 
              :src="template.url" 
              class="img-fluid border rounded shadow-sm" 
              style="max-height: 400px; object-fit: contain; max-width: none;" 
              :alt="template.text"
            >
            <div
              class="small text-muted text-center mt-2 text-truncate"
              style="max-width: 300px;"
            >
              {{ template.text }}
            </div>
          </div>
        </div>

        <b-form-checkbox
          v-model="createNewTemplatesForEditableImages"
          class="mb-4"
          :disabled="isLoading"
        >
          Create new templates for the editable images
        </b-form-checkbox>

        <!-- Options section -->
        <!--
        <div
          class="d-flex justify-content-between align-items-center p-2 cursor-pointer border rounded mt-2 mb-2"
          @click="isCollapsed = !isCollapsed"
        >
          <h6 class="mb-0">
            <span><i class="bi bi-sliders mr-1" />Options</span>
          </h6>
          <i :class="['fas', isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" />
        </div>
        <div v-show="!isCollapsed">
          <div class="row mb-3">
            <div class="col-md-12">
              <label>Template action:</label>
              <b-form-select
                v-model="templateAction"
                :options="templateActionOptions"
              />
            </div>
          </div>
        </div>
        -->

        <!-- Error Alert -->
        <b-alert
          :show="!!errorMessage"
          variant="danger"
          dismissible
          class="mb-3"
          @dismissed="errorMessage = ''"
        >
          {{ errorMessage }}
        </b-alert>

        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedTemplates.length === 0 || isLoading"
            @click="handleExtractText"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
            />
            {{ isLoading ? 'Analyzing...' : (areAllSelectedImagesCached ? 'Continue' : 'Analyze Image(s)') }}
          </button>
        </div>
      </div>

      <!-- Step 2: Selection of texts -->
      <div v-if="currentStep === 2">
        <div
          v-if="selectedTemplates.length > 0"
          class="template-navigation mb-3 p-2 border rounded d-flex justify-content-between align-items-center bg-light"
        >
          <div>
            <strong>Template {{ currentTemplateIndex + 1 }} of {{ selectedTemplates.length }}</strong> — {{ currentTemplateOption?.text }}
          </div>
          <div class="d-flex align-items-center gap-1">
            <button 
              v-for="(template, index) in selectedTemplates" 
              :key="template.value"
              class="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
              :class="index === currentTemplateIndex ? 'btn-success text-white' : 'btn-outline-secondary'"
              style="width: 24px; height: 24px; font-size: 12px; margin-left: 4px;"
              :disabled="isAnalyzingRegion"
              @click="currentTemplateIndex = index"
            >
              {{ index + 1 }}
            </button>

            <button 
              type="button" 
              class="btn btn-outline-secondary btn-sm ml-2" 
              :disabled="currentTemplateIndex === 0 || isAnalyzingRegion"
              @click="currentTemplateIndex--"
            >
              <i class="bi bi-chevron-left" /> Prev
            </button>
            <button 
              type="button" 
              class="btn btn-outline-secondary btn-sm" 
              :disabled="currentTemplateIndex >= Math.max(0, selectedTemplates.length - 1) || isAnalyzingRegion"
              @click="currentTemplateIndex++"
            >
              Next <i class="bi bi-chevron-right" />
            </button>
          </div>
        </div>

        <div class="row mb-2 align-items-center">
          <div class="col-md-5 d-flex justify-content-between align-items-center">
            <h6 class="mb-0 font-weight-bold">
              Detected Texts
            </h6>
            <div class="small">
              <button
                class="btn btn-link btn-sm p-0 mr-2 text-success font-weight-bold"
                style="text-decoration: none;"
                @click="selectAllTexts"
              >
                Select All
              </button>
              <button
                class="btn btn-link btn-sm p-0 text-danger font-weight-bold"
                style="text-decoration: none;"
                @click="deselectAutomaticTexts"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div class="col-md-7">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold">
                Preview
              </h6>
              <button
                type="button"
                class="btn btn-sm"
                :class="drawingMode ? 'btn-primary' : 'btn-outline-primary'"
                :disabled="isLoading || isAnalyzingRegion || !nativeWidth || !nativeHeight"
                @click="toggleDrawingMode"
              >
                <span
                  v-if="isAnalyzingRegion"
                  class="spinner-border spinner-border-sm mr-1"
                />
                <i
                  v-else
                  class="bi bi-bounding-box mr-1"
                />
                {{ isAnalyzingRegion ? 'Analyzing...' : (drawingMode ? 'Cancel drawing' : 'Add missing text') }}
              </button>
            </div>
          </div>
        </div>

        <div class="row align-items-stretch">
          <div class="col-md-5 d-flex flex-column">
            <p class="small text-muted mb-2">
              Select the text you want to make editable. Expand a row to adjust color, size or font.
            </p>
            <div
              class="text-list-container mb-3 flex-grow-1 d-flex flex-column"
              :style="{
                height: '500px',
                maxHeight: isLoading ? '60vh' : '70vh'
              }"
            >
              <div
                :key="`text-list-${analysisRenderRevision}`"
                class="text-list-scroll flex-grow-1"
                style="overflow-y: auto; min-height: 0;"
              >
                <div
                  v-for="(tp, index) in extractedTexts"
                  :key="tp._clientId"
                  :ref="'textRow-' + tp._clientId"
                  class="detected-text-row"
                  :class="{
                    'is-hovered': hoveredIndex === index,
                    'active-text-row': activeTextId === tp._clientId,
                    'is-selected': isTextSelected(index)
                  }"
                  @mouseenter="hoveredIndex = index"
                  @mouseleave="hoveredIndex = null"
                >
                  <div
                    class="detected-text-row__header"
                    role="button"
                    tabindex="0"
                    :aria-expanded="isTextExpanded(tp._clientId) ? 'true' : 'false'"
                    @click="toggleTextDetails(tp._clientId)"
                    @keydown.enter="toggleTextDetails(tp._clientId)"
                    @keydown.space.prevent="toggleTextDetails(tp._clientId)"
                  >
                    <div
                      class="custom-control custom-checkbox flex-grow-1"
                      @click.stop
                    >
                      <input
                        :id="'tp-' + tp._clientId"
                        v-model="selectedIndices"
                        type="checkbox"
                        class="custom-control-input"
                        :value="index"
                        :disabled="tp._manual"
                      >
                      <label
                        :for="'tp-' + tp._clientId"
                        class="custom-control-label cursor-pointer w-100"
                      >
                        <strong :style="{ fontFamily: tp.font || 'inherit' }">"{{ tp.content }}"</strong>
                      </label>
                    </div>
                    <button
                      v-if="tp._manual"
                      type="button"
                      class="btn btn-link btn-sm text-danger p-1 ml-2"
                      title="Delete manual text region"
                      @click.stop="deleteManualText(index)"
                    >
                      <i class="bi bi-trash" />
                    </button>
                    <button
                      type="button"
                      class="detected-text-row__toggle"
                      :aria-label="isTextExpanded(tp._clientId) ? 'Collapse text details' : 'Expand text details'"
                      @click.stop="toggleTextDetails(tp._clientId)"
                    >
                      <i :class="['bi', isTextExpanded(tp._clientId) ? 'bi-chevron-up' : 'bi-chevron-down']" />
                    </button>
                  </div>
                  <div
                    v-if="isTextExpanded(tp._clientId)"
                    class="detected-text-row__details"
                  >
                    <div class="detected-text-row__metadata">
                      <span>Color</span>
                      <input
                        v-model="tp.color"
                        type="color"
                        class="detected-text-row__color"
                        :title="`Text color: ${tp.color || '#000000'}`"
                        aria-label="Text color"
                      >
                      <span class="detected-text-row__separator" />
                      <label class="detected-text-row__size mb-0">
                        <span>Size:</span>
                        <input
                          v-model.number="tp.font_size"
                          type="number"
                          min="1"
                          step="1"
                          aria-label="Font size"
                          @change="roundFontSize(tp)"
                        >
                        <span>px</span>
                      </label>
                    </div>
                    <FontSelector
                      v-model="tp.font"
                      class="detected-text-row__font"
                      :all-fonts="true"
                    />
                  </div>
                </div>
              </div>
              <div class="make-editable-options flex-shrink-0">
                <b-form-checkbox
                  v-model="translateExtractedText"
                  :disabled="isLoading || !createNewTemplatesForEditableImages"
                >
                  Translate extracted text
                </b-form-checkbox>
                <div
                  v-if="!createNewTemplatesForEditableImages"
                  class="small text-muted mt-1"
                >
                  Translation is available only when creating new templates.
                </div>
                <div
                  v-if="translateExtractedText"
                  class="mt-2"
                >
                  <label class="font-weight-bold mb-1">Target language(s):</label>
                  <Multiselect
                    v-model="translateTargetLanguages"
                    :options="availableLanguages"
                    :multiple="true"
                    :close-on-select="true"
                    :taggable="false"
                    :disabled="isLoading"
                    placeholder="Select language(s)..."
                    class="multiselect-translation"
                  />
                  <div
                    v-if="translateTargetLanguages.length === 0"
                    class="small text-danger mt-1"
                  >
                    Select at least one target language.
                  </div>
                </div>
                <button
                  type="button"
                  class="advanced-toggle"
                  :aria-expanded="isAdvancedOpen ? 'true' : 'false'"
                  @click="isAdvancedOpen = !isAdvancedOpen"
                >
                  Advanced
                  <i :class="['bi', isAdvancedOpen ? 'bi-chevron-up' : 'bi-chevron-down']" />
                </button>
                <b-collapse v-model="isAdvancedOpen">
                  <div class="advanced-options">
                    <label
                      class="font-weight-bold mb-1"
                      for="textRemovalMethodV2"
                    >Text removal method</label>
                    <b-form-select
                      id="textRemovalMethodV2"
                      v-model="textRemovalMethod"
                      :options="textRemovalMethodOptions"
                      :disabled="isLoading"
                    />
                    <b-form-checkbox
                      v-if="textRemovalMethod === 'opencv'"
                      v-model="enableAutoFallback"
                      class="mt-2 mb-0"
                      :disabled="isLoading"
                    >
                      <span class="small">Auto-fallback to GPT Image 2</span>
                    </b-form-checkbox>
                  </div>
                </b-collapse>
              </div>
            </div>
          </div>
          <div class="col-md-7">
            <p class="small text-muted mb-2">
              <span v-if="drawingMode">Drag over the complete missing text region.</span>
              <span v-else>&nbsp;</span>
            </p>
            <div
              ref="previewContainer"
              class="preview-container border rounded position-relative text-center p-0"
              :style="{
                background: '#e9ecef',
                overflow: 'auto',
                minHeight: isLoading ? '0' : '500px',
                maxHeight: isLoading ? '60vh' : '70vh'
              }"
            >
              <div class="position-relative d-inline-block">
                <img 
                  v-if="selectedImage || (currentTemplateOption && currentTemplateOption.url)"
                  :key="currentTemplateOption ? `${currentTemplateOption.value}-${analysisRenderRevision}` : `img-${analysisRenderRevision}`"
                  :src="selectedImage || currentTemplateOption.url" 
                  class="img-fluid" 
                  alt="Original Preview"
                  style="display: block; max-width: 100%; height: auto;"
                  @load="onPreviewLoad"
                >
                <!-- Dynamic SVG overlay for detected text boxes -->
                <svg
                  v-if="targetElement"
                  ref="previewOverlay"
                  class="position-absolute" 
                  :viewBox="`0 0 ${nativeWidth} ${nativeHeight}`"
                  :class="{'drawing-overlay': drawingMode}"
                  :style="{top: 0, left: 0, width: '100%', height: '100%', pointerEvents: drawingMode ? 'auto' : 'none'}"
                  @pointerdown="startDrawing"
                  @pointermove="updateDrawing"
                  @pointerup="finishDrawing"
                  @pointercancel="cancelDrawing"
                >
                  <rect 
                    v-for="idx in selectedIndices" 
                    :key="'rect-'+idx" 
                    :x="extractedTexts[idx].pos_x" 
                    :y="extractedTexts[idx].pos_y" 
                    :width="extractedTexts[idx].width" 
                    :height="extractedTexts[idx].height" 
                    fill="rgba(255, 193, 7, 0.2)"
                    stroke="#ffc107"
                    stroke-width="4"
                    :style="{pointerEvents: !drawingMode ? 'all' : 'none', cursor: 'pointer'}"
                    @click.stop="focusText(idx)"
                  />
                  <!-- Hover highlight -->
                  <rect 
                    v-if="highlightedTextIndex !== null && extractedTexts[highlightedTextIndex]"
                    :x="extractedTexts[highlightedTextIndex].pos_x"
                    :y="extractedTexts[highlightedTextIndex].pos_y"
                    :width="extractedTexts[highlightedTextIndex].width"
                    :height="extractedTexts[highlightedTextIndex].height"
                    fill="transparent" 
                    stroke="#ffc107" 
                    stroke-width="10"
                  />
                  <rect
                    v-if="drawingRect"
                    :x="drawingRect.x"
                    :y="drawingRect.y"
                    :width="drawingRect.width"
                    :height="drawingRect.height"
                    fill="rgba(255, 193, 7, 0.2)"
                    stroke="#ffc107"
                    stroke-width="5"
                    stroke-dasharray="14 8"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Alert -->
        <b-alert
          :show="!!errorMessage"
          variant="danger"
          dismissible
          class="mb-3 mt-3"
          @dismissed="errorMessage = ''"
        >
          {{ errorMessage }}
        </b-alert>
      </div>

      <template #modal-footer>
        <div class="w-100">
          <!-- General Progress indicator (visible across steps when loading) -->
          <div
            v-if="isLoading"
            class="px-3 py-2"
          >
            <div class="d-flex align-items-center mb-2">
              <span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              />
              <span>{{ progressText }}</span>
            </div>
            <b-progress
              :value="progressPercent"
              :max="100"
              animated
              variant="info"
              style="height: 6px;"
            />
          </div>
          <div
            v-if="currentStep === 2"
            class="d-flex justify-content-between align-items-center px-3 py-2 border-top"
          >
            <div class="d-flex align-items-center">
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="isLoading || isAnalyzingRegion"
                @click="currentStep = 1"
              >
                Back
              </button>
              <button
                type="button"
                class="btn btn-outline-warning ml-2"
                :disabled="isLoading || isAnalyzingRegion"
                @click="handleReanalyze"
              >
                <i class="fas fa-redo mr-1" />Re-analyze
              </button>
            </div>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isLoading || isAnalyzingRegion || (translateExtractedText && translateTargetLanguages.length === 0)"
              @click="handleRemoveText"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              />
              {{ isLoading ? 'Processing...' : 'Make Editable' }}
            </button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { makeEditableV2EventBroker, EVENTS } from '@frontend/group/modules/make-editable-v2/event-broker';
import { FIELD_TYPE_BACKGROND_THEME_IMAGE } from "@frontend/constants/type-fields-of-template";
import { addAddField } from '@frontend/group/modules/additional-fields';
import { deleteAdditionalBackgroundThemeImageField } from '@frontend/group/modules/additional-fields/deleting';
import { deleteDisplayBuilderBackgroundLayer } from '@frontend/group/modules/display-builder/delete-background-layer';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { deleteURLParameters, setURLParameters } from "@frontend/utils";
import parseText from "@/common/preview/parse-text";
import Multiselect from 'vue-multiselect';
import FontSelector from '@frontend/components/modules/layouts-page/EditorControl/components/FontSelector.vue';

const getShowPageScope = () => ShowPageScopeCollector.get(['saveLayout', 'updateLayoutChanges', 'canvas_data', 'getChangesLayoutById']);

const MODAL_ID = 'make-editable-v2-modal';
const ANALYSIS_CACHE_STORAGE_KEY = 'make-editable-v2-analysis-cache';
const ANALYSIS_CACHE_VERSION = 1;
const ANALYSIS_CACHE_MAX_LAYOUTS = 5;
const ANALYSIS_CACHE_MAX_ENTRIES_PER_LAYOUT = 50;

export default {
    name: 'MakeEditableV2Modal',
    components: {
        FontSelector,
        Multiselect,
    },
    data() {
        return {
            MODAL_ID,
            isOpen: false,
            backgroundImages: [],
            selectedTemplates: [],
            createNewTemplatesForEditableImages: true,
            templateConfigurations: {},
            analysisCache: {},
            analysisRenderRevision: 0,
            currentTemplateIndex: 0,
            isLoading: false,
            // Workflow status
            currentStep: 1,
            extractedTexts: [],
            selectedIndices: [],
            detectionImageUrl: null,
            hoveredIndex: null,
            activeTextId: null,
            expandedTextIds: [],
            drawingMode: false,
            drawingStart: null,
            drawingRect: null,
            isAnalyzingRegion: false,
            nextTextClientId: 1,
            nativeWidth: 0,
            nativeHeight: 0,
            // Data for currently active template
            instanceId: null,
            preview: null,
            targetElement: null,
            selectedImage: null,
            // Options
            templateAction: 'update_current',
            templateActionOptions: [
                { value: 'update_current', text: 'Update selected template' },
                { value: 'backup_then_update', text: 'Backup original, then update' }
            ],
            textRemovalMethod: 'opencv',
            enableAutoFallback: false,
            textRemovalMethodOptions: [
                { value: 'opencv', text: 'OpenCV (Faster)' },
                { value: 'gpt-image-2', text: 'Nano Banana 2 (Higher quality, slower)' }
            ],
            translateExtractedText: false,
            translateTargetLanguages: [],
            availableLanguages: [
                'Arabic', 'Bengali', 'Cantonese', 'Czech', 'Dutch', 'English',
                'French-CA', 'French-FR', 'German', 'Greek', 'Hebrew', 'Hindi',
                'Italian', 'Japanese', 'Korean', 'Polish', 'Portuguese',
                'Russian', 'Simplified Chinese', 'Slovak', 'Spanish (LatAm)', 'Spanish (Spain/EU)',
                'Swedish', 'Thai', 'Traditional Chinese', 'Turkish', 'Vietnamese'
            ],
            isAdvancedOpen: false,
            // Progress
            progressText: '',
            progressPercent: 0,
            progressTimer: null,
            progressStages: [],
            layoutId: 0,
            errorMessage: '',
        }
    },
    computed: {
        ...mapGetters("layout", ["selectedInstance"]),
        modalTitle() {
            if (this.currentStep === 1) return "Make Editable V2 - Step 1/2";
            if (this.currentStep === 2) return "Make Editable V2 - Step 2/2";
            return "Make Editable";
        },
        currentTemplateOption() {
            return this.selectedTemplates[this.currentTemplateIndex] || null;
        },
        areAllSelectedImagesCached() {
            return this.selectedTemplates.length > 0 && this.selectedTemplates.every(template => {
                const imageUrl = this.getAnalysisImageUrl(template);
                const cacheKey = this.getAnalysisCacheKey(template, imageUrl);

                return Object.prototype.hasOwnProperty.call(this.analysisCache, cacheKey);
            });
        },
        groupedBackgroundImages() {
            const groups = new Map();

            this.backgroundImages.forEach(image => {
                if (!groups.has(image.instanceId)) {
                    groups.set(image.instanceId, {
                        instanceId: image.instanceId,
                        templateName: image.templateName,
                        images: [],
                    });
                }
                groups.get(image.instanceId).images.push(image);
            });

            return Array.from(groups.values());
        },
        hasFullySelectedGroup() {
            const selectedValues = new Set(this.selectedTemplates.map(image => image.value));
            return this.groupedBackgroundImages.some(group => (
                group.images.length > 1
                && group.images.every(image => selectedValues.has(image.value))
            ));
        },
        highlightedTextIndex() {
            if (this.hoveredIndex !== null) return this.hoveredIndex;
            if (!this.activeTextId) return null;
            const index = this.extractedTexts.findIndex(text => text._clientId === this.activeTextId);
            return index >= 0 ? index : null;
        }
    },
    watch: {
        hasFullySelectedGroup(isFullySelected) {
            if (isFullySelected) {
                this.createNewTemplatesForEditableImages = false;
            }
        },
        createNewTemplatesForEditableImages(createNewTemplates) {
            if (!createNewTemplates) {
                this.translateExtractedText = false;
                this.translateTargetLanguages = [];
            }
        },
        currentTemplateIndex(newIdx, oldIdx) {
            // Save state for oldIdx
            if (oldIdx !== undefined && this.selectedTemplates[oldIdx]) {
                const oldVal = this.selectedTemplates[oldIdx].value;
                this.$set(this.templateConfigurations, oldVal, {
                    extractedTexts: this.extractedTexts,
                    selectedIndices: [...this.selectedIndices],
                    detectionImageUrl: this.detectionImageUrl,
                });
            }
            // Load state for newIdx
            if (newIdx !== undefined && this.selectedTemplates[newIdx]) {
                const newVal = this.selectedTemplates[newIdx].value;
                const config = this.templateConfigurations[newVal] || {};
                this.extractedTexts = config.extractedTexts || [];
                this.selectedIndices = config.selectedIndices ? [...config.selectedIndices] : [];
                this.detectionImageUrl = config.detectionImageUrl || null;
                this.activeTextId = null;
                this.drawingMode = false;
                this.drawingStart = null;
                this.drawingRect = null;
                this.nativeWidth = 0;
                this.nativeHeight = 0;

                const template = this.selectedTemplates[newIdx];
                this.targetElement = template.target;
                this.instanceId = template.instanceId;
                this.preview = template.preview;
                
                let originalSrc = deleteURLParameters(this.targetElement?.getSrc(), ['lightweight', 'full_resolution']);
                originalSrc = setURLParameters(originalSrc, { full_resolution: true });
                this.selectedImage = originalSrc;
            }
        }
    },
    mounted() {
        makeEditableV2EventBroker.on(EVENTS.INIT, this.init.bind(this));
    },
    beforeDestroy() {
        this.stopSimulatedProgress();
    },
    methods: {
        init(data) {
            this.backgroundImages = (data.allBackgroundImages || []).map(img => ({
                value: img.instanceId + '_' + img.id,
                text: img.name,
                templateName: img.templateName || img.preview?.template?.name || 'Template',
                url: img.url,
                thumbnail: img.thumbnail,
                target: img.target,
                instanceId: img.instanceId,
                preview: img.preview
            }));
            
            this.currentStep = 1;
            this.currentTemplateIndex = 0;
            this.templateConfigurations = {};
            this.extractedTexts = [];
            this.selectedIndices = [];
            this.detectionImageUrl = null;
            this.activeTextId = null;
            this.expandedTextIds = [];
            this.drawingMode = false;
            this.drawingStart = null;
            this.drawingRect = null;
            this.isAnalyzingRegion = false;
            this.nextTextClientId = 1;
            this.nativeWidth = 0;
            this.nativeHeight = 0;
            this.selectedTemplates = [];
            this.createNewTemplatesForEditableImages = true;
            this.textRemovalMethod = 'opencv';
            this.enableAutoFallback = false;
            this.translateExtractedText = false;
            this.translateTargetLanguages = [];
            this.isAdvancedOpen = false;
            this.errorMessage = '';

            // Extract layout ID from URL
            const url = window.location.href;
            const match = url.match(/\/group\/(\d+)\//);
            if (match) {
                this.layoutId = parseInt(match[1], 10);
            }
            this.analysisCache = this.loadStoredAnalysisCache();

            if (this.backgroundImages.length > 0) {
                const defaultItems = this.backgroundImages.filter(img => img.instanceId == data.clickedInstanceId);
                this.selectedTemplates = defaultItems.length > 0 ? [defaultItems[0]] : [this.backgroundImages[0]];
            }
            const cachedResults = this.getSelectedCachedAnalysisResults();
            if (cachedResults) {
                this.applyAnalysisResults(cachedResults);
            }
            this.$bvModal.show(this.MODAL_ID);
        },
        onModalOpen() {
            this.isOpen = true;
        },
        onModalHide() {
            this.isOpen = false;
            this.stopSimulatedProgress();
            this.progressText = '';
            this.progressPercent = 0;
            this.isLoading = false;
            this.errorMessage = '';
            this.activeTextId = null;
            this.drawingMode = false;
            this.drawingStart = null;
            this.drawingRect = null;
            this.isAnalyzingRegion = false;
        },
        
        selectAllFirstImages() {
            const firstImages = [];
            const seen = new Set();
            for (const img of this.backgroundImages) {
                if (!seen.has(img.instanceId)) {
                    seen.add(img.instanceId);
                    firstImages.push(img);
                }
            }
            this.selectedTemplates = firstImages;
        },

        getTemplateGroupName(instanceId) {
            return this.groupedBackgroundImages.find(group => group.instanceId == instanceId)?.templateName || 'Template';
        },

        onPreviewLoad(event) {
            const img = event.target;
            this.nativeWidth = img.naturalWidth || 0;
            this.nativeHeight = img.naturalHeight || 0;
        },

        createTextClientId() {
            return `make-editable-text-${this.nextTextClientId++}`;
        },

        getAnalysisImageUrl(template) {
            let imageUrl = deleteURLParameters(
                template.target?.getSrc() || template.url,
                ['lightweight', 'full_resolution']
            );
            imageUrl = setURLParameters(imageUrl, {full_resolution: true});

            return imageUrl.toString();
        },

        getAnalysisCacheKey(template, imageUrl) {
            return `${template.value}|${imageUrl}`;
        },

        getSelectedCachedAnalysisResults() {
            if (this.selectedTemplates.length === 0) return null;

            const results = [];
            for (const template of this.selectedTemplates) {
                const imageUrl = this.getAnalysisImageUrl(template);
                const cacheKey = this.getAnalysisCacheKey(template, imageUrl);
                if (!Object.prototype.hasOwnProperty.call(this.analysisCache, cacheKey)) {
                    return null;
                }
                results.push({template, data: this.analysisCache[cacheKey]});
            }

            return results;
        },

        readAnalysisCacheStore() {
            const emptyStore = {version: ANALYSIS_CACHE_VERSION, layouts: {}};
            try {
                const storedValue = window.localStorage.getItem(ANALYSIS_CACHE_STORAGE_KEY);
                if (!storedValue) return emptyStore;

                const store = JSON.parse(storedValue);
                if (store?.version !== ANALYSIS_CACHE_VERSION || !store.layouts) {
                    return emptyStore;
                }

                return store;
            } catch (error) {
                console.warn('Unable to read the Make Editable analysis cache.', error);
                return emptyStore;
            }
        },

        writeAnalysisCacheStore(store) {
            try {
                window.localStorage.setItem(ANALYSIS_CACHE_STORAGE_KEY, JSON.stringify(store));
            } catch (error) {
                console.warn('Unable to persist the Make Editable analysis cache.', error);
            }
        },

        pruneAnalysisCacheLayouts(store) {
            const layoutIds = Object.keys(store.layouts).sort((left, right) => (
                store.layouts[right].lastAccessed - store.layouts[left].lastAccessed
            ));
            layoutIds.slice(ANALYSIS_CACHE_MAX_LAYOUTS).forEach(layoutId => {
                delete store.layouts[layoutId];
            });
        },

        loadStoredAnalysisCache() {
            if (!this.layoutId) return {};

            const store = this.readAnalysisCacheStore();
            const layout = store.layouts[String(this.layoutId)];
            if (!layout) {
                this.writeAnalysisCacheStore(store);
                return {};
            }

            layout.lastAccessed = Date.now();
            this.pruneAnalysisCacheLayouts(store);
            this.writeAnalysisCacheStore(store);

            return Object.fromEntries(
                Object.entries(layout.entries).map(([cacheKey, entry]) => [cacheKey, entry.data])
            );
        },

        cacheAnalysisResult(cacheKey, data) {
            const cachedData = {
                success: true,
                text_positions: data.text_positions || [],
                background_image_url: data.background_image_url || null,
                recommended_removal_method: data.recommended_removal_method || null,
            };
            this.$set(this.analysisCache, cacheKey, cachedData);

            if (!this.layoutId) return;

            const now = Date.now();
            const store = this.readAnalysisCacheStore();
            const layoutId = String(this.layoutId);
            const layout = store.layouts[layoutId] || {lastAccessed: now, entries: {}};
            layout.lastAccessed = now;
            layout.entries[cacheKey] = {lastAccessed: now, data: cachedData};

            const entryKeys = Object.keys(layout.entries).sort((left, right) => (
                layout.entries[right].lastAccessed - layout.entries[left].lastAccessed
            ));
            entryKeys.slice(ANALYSIS_CACHE_MAX_ENTRIES_PER_LAYOUT).forEach(entryKey => {
                delete layout.entries[entryKey];
            });

            store.layouts[layoutId] = layout;
            this.pruneAnalysisCacheLayouts(store);
            this.writeAnalysisCacheStore(store);
        },

        clearSelectedAnalysisCache() {
            const cacheKeys = this.selectedTemplates.map(template => {
                const imageUrl = this.getAnalysisImageUrl(template);
                return this.getAnalysisCacheKey(template, imageUrl);
            });
            cacheKeys.forEach(cacheKey => this.$delete(this.analysisCache, cacheKey));

            if (!this.layoutId) return;

            const store = this.readAnalysisCacheStore();
            const layoutId = String(this.layoutId);
            const layout = store.layouts[layoutId];
            if (!layout) return;

            cacheKeys.forEach(cacheKey => delete layout.entries[cacheKey]);
            if (Object.keys(layout.entries).length === 0) {
                delete store.layouts[layoutId];
            } else {
                layout.lastAccessed = Date.now();
            }
            this.writeAnalysisCacheStore(store);
        },

        handleReanalyze() {
            this.clearSelectedAnalysisCache();
            this.handleExtractText();
        },

        selectAllTexts() {
            this.selectedIndices = this.extractedTexts.map((_, index) => index);
        },

        deselectAutomaticTexts() {
            this.selectedIndices = this.extractedTexts
                .map((text, index) => text._manual ? index : null)
                .filter(index => index !== null);
        },

        isTextSelected(index) {
            return this.selectedIndices.includes(index);
        },

        isTextExpanded(clientId) {
            return this.expandedTextIds.includes(clientId);
        },

        toggleTextDetails(clientId) {
            if (this.isTextExpanded(clientId)) {
                this.expandedTextIds = this.expandedTextIds.filter(id => id !== clientId);
                return;
            }

            this.expandedTextIds = [...this.expandedTextIds, clientId];
        },

        roundFontSize(textBlock) {
            textBlock.font_size = Math.max(1, Math.round(Number(textBlock.font_size) || 1));
        },

        applyAnalysisResults(results) {
            this.templateConfigurations = {};
            results.forEach(({template, data}) => {
                const extractedTexts = (data.text_positions || []).map(textBlock => ({
                    ...textBlock,
                    font: textBlock.font || '',
                    font_size: Math.round(Number(textBlock.font_size) || 0),
                    _manual: false,
                    _clientId: this.createTextClientId(),
                }));
                this.$set(this.templateConfigurations, template.value, {
                    extractedTexts,
                    selectedIndices: extractedTexts.map((_, idx) => idx),
                    detectionImageUrl: data.background_image_url
                });
            });

            this.textRemovalMethod = results.some(
                ({data}) => data.recommended_removal_method === 'gpt-image-2'
            ) ? 'gpt-image-2' : 'opencv';
            this.analysisRenderRevision++;

            const template = this.selectedTemplates[0];
            const config = this.templateConfigurations[template.value] || {};
            this.extractedTexts = config.extractedTexts || [];
            this.selectedIndices = config.selectedIndices ? [...config.selectedIndices] : [];
            this.detectionImageUrl = config.detectionImageUrl || null;
            this.targetElement = template.target;
            this.instanceId = template.instanceId;
            this.preview = template.preview;
            this.nativeWidth = 0;
            this.nativeHeight = 0;
            this.selectedImage = this.getAnalysisImageUrl(template);
            this.currentStep = 2;
        },

        toggleDrawingMode() {
            this.drawingMode = !this.drawingMode;
            this.drawingStart = null;
            this.drawingRect = null;
            this.activeTextId = null;
        },

        getPreviewPoint(event) {
            const overlay = this.$refs.previewOverlay;
            if (!overlay || !this.nativeWidth || !this.nativeHeight) return null;
            const bounds = overlay.getBoundingClientRect();
            return {
                x: Math.max(0, Math.min(this.nativeWidth, (event.clientX - bounds.left) * this.nativeWidth / bounds.width)),
                y: Math.max(0, Math.min(this.nativeHeight, (event.clientY - bounds.top) * this.nativeHeight / bounds.height)),
            };
        },

        startDrawing(event) {
            if (!this.drawingMode || this.isAnalyzingRegion) return;
            const point = this.getPreviewPoint(event);
            if (!point) return;
            event.preventDefault();
            this.$refs.previewOverlay?.setPointerCapture?.(event.pointerId);
            this.drawingStart = point;
            this.drawingRect = {x: point.x, y: point.y, width: 0, height: 0};
        },

        updateDrawing(event) {
            if (!this.drawingStart) return;
            const point = this.getPreviewPoint(event);
            if (!point) return;
            event.preventDefault();
            this.drawingRect = {
                x: Math.min(this.drawingStart.x, point.x),
                y: Math.min(this.drawingStart.y, point.y),
                width: Math.abs(point.x - this.drawingStart.x),
                height: Math.abs(point.y - this.drawingStart.y),
            };
        },

        async finishDrawing(event) {
            if (!this.drawingStart || !this.drawingRect) return;
            event.preventDefault();
            const selection = {...this.drawingRect};
            this.drawingStart = null;
            this.drawingMode = false;

            if (selection.width < 5 || selection.height < 5) {
                this.drawingRect = null;
                return;
            }

            await this.analyzeManualRegion(selection);
        },

        cancelDrawing() {
            this.drawingStart = null;
            this.drawingRect = null;
        },

        async analyzeManualRegion(selection) {
            this.isAnalyzingRegion = true;
            try {
                const response = await axios.post('/ocr/make-editable-v2/analyze-text-region', {
                    image_url: this.selectedImage || this.currentTemplateOption?.url,
                    pos_x: Math.round(selection.x),
                    pos_y: Math.round(selection.y),
                    width: Math.round(selection.width),
                    height: Math.round(selection.height),
                });
                const data = response.data;
                if (!data.success) {
                    throw new Error(data.message || 'The selected text region could not be analyzed.');
                }
                if (!data.text_detected) {
                    toastr.error('No text was detected.');
                    return;
                }

                if (data.contains_multiple_text_blocks) {
                    const reasonLabels = {
                        spatial_separation: 'the text regions are too far apart',
                        font_size_difference: 'the text uses materially different font sizes',
                        color_difference: 'the text uses materially different colors',
                        multiple_differences: 'the text regions differ in spacing or styling',
                    };
                    const result = await Swal.fire({
                        title: 'Multiple text blocks detected',
                        text: `This selection appears to contain multiple blocks because ${reasonLabels[data.multiple_text_blocks_reason] || 'their layout or styling differs'}. Add it as one editable text block anyway?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Yes',
                        cancelButtonText: 'No',
                    });
                    if (!result.isConfirmed) return;
                }

                const textBlock = {
                    ...data.text_position,
                    font: data.text_position.font || '',
                    font_size: Math.round(Number(data.text_position.font_size) || 0),
                    _manual: true,
                    _clientId: this.createTextClientId(),
                };
                const index = this.extractedTexts.length;
                this.extractedTexts.push(textBlock);
                this.selectedIndices.push(index);
                this.focusText(index);
            } catch (error) {
                console.error('Failed to analyze manual text region', error);
                toastr.error(error.response?.data?.message || error.message || 'The selected text region could not be analyzed.');
            } finally {
                this.isAnalyzingRegion = false;
                this.drawingRect = null;
            }
        },

        focusText(index) {
            const text = this.extractedTexts[index];
            if (!text) return;
            this.activeTextId = text._clientId;
            this.$nextTick(() => {
                const rowRef = this.$refs[`textRow-${text._clientId}`];
                const row = Array.isArray(rowRef) ? rowRef[0] : rowRef;
                row?.scrollIntoView?.({behavior: 'smooth', block: 'nearest'});
            });
        },

        deleteManualText(index) {
            const text = this.extractedTexts[index];
            if (!text?._manual) return;
            this.extractedTexts.splice(index, 1);
            this.expandedTextIds = this.expandedTextIds.filter(id => id !== text._clientId);
            this.selectedIndices = this.selectedIndices
                .filter(selectedIndex => selectedIndex !== index)
                .map(selectedIndex => selectedIndex > index ? selectedIndex - 1 : selectedIndex);
            if (this.activeTextId === text._clientId) this.activeTextId = null;
            this.hoveredIndex = null;
        },

        setProgress(percent, text) {
            this.progressText = text;
            if (percent >= 100) {
                this.finishSimulatedProgress();
                return;
            }
            this.progressPercent = Math.max(this.progressPercent, percent);
        },

        startSimulatedProgress(stages) {
            this.stopSimulatedProgress();
            this.progressPercent = 4;
            this.progressStages = stages;

            const advance = () => {
                if (!this.isLoading) {
                    this.stopSimulatedProgress();
                    return;
                }

                const progress = this.progressPercent;
                let increment;
                if (progress < 35) {
                    increment = 0.7 + Math.random();
                } else if (progress < 70) {
                    increment = 0.3 + Math.random() * 0.5;
                } else if (progress < 88) {
                    increment = 0.1 + Math.random() * 0.25;
                } else {
                    increment = (94 - progress) * (0.015 + Math.random() * 0.015);
                }

                this.progressPercent = progress + increment;
                const activeStage = [...this.progressStages]
                    .reverse()
                    .find(stage => this.progressPercent >= stage.at);
                if (activeStage) this.progressText = activeStage.text;

                const nextDelay = 550 + Math.round(Math.random() * 450);
                this.progressTimer = setTimeout(advance, nextDelay);
            };

            const initialStage = this.progressStages[0];
            if (initialStage) this.progressText = initialStage.text;
            this.progressTimer = setTimeout(advance, 650);
        },

        finishSimulatedProgress() {
            this.stopSimulatedProgress();
            const finish = () => {
                const remaining = 100 - this.progressPercent;
                this.progressPercent = Math.min(
                    100,
                    this.progressPercent + Math.max(1, remaining * 0.35)
                );
                if (this.progressPercent < 100) {
                    this.progressTimer = setTimeout(finish, 60);
                } else {
                    this.progressTimer = null;
                }
            };
            finish();
        },

        stopSimulatedProgress() {
            if (this.progressTimer) {
                clearTimeout(this.progressTimer);
                this.progressTimer = null;
            }
            this.progressStages = [];
        },

        async createBackupCopy(templateObj) {
            try {
                this.setProgress(5, 'Creating backup copy of original template...');

                const grid = $("#template-group-preview");
                let customerId = templateObj.preview?.template?.customer_id || grid.attr("data-customer-id");
                const layoutId = grid.attr("data-layout-id");
                const templateId = templateObj.preview?.template?.id || grid.attr("data-template-id");

                const isAllCustomer = parseInt(customerId) === 0;
                if (isAllCustomer && templateObj.preview?.template?.customer_id) {
                    customerId = templateObj.preview.template.customer_id;
                }

                const url = `/banner/${customerId}/group/${layoutId}/template/${templateObj.instanceId}/${templateId}/duplicate?old-customer=0`;
                const response = await axios.post(url, {
                    forceReturnInstanceId: true
                });

                if (response.data && response.data.instance_id) {
                    toastr.success('Backup copy created!');
                    return true;
                } else {
                    throw new Error('Failed to create backup');
                }
            } catch (error) {
                console.error('Error creating backup copy:', error);
                this.errorMessage = 'Failed to create backup copy. Please try again.';
                return false;
            }
        },

        async handleExtractText() {
            if (this.selectedTemplates.length === 0) return;

            const usingCachedAnalysis = this.areAllSelectedImagesCached;
            this.isLoading = true;
            this.errorMessage = '';
            this.startSimulatedProgress([
                {at: 0, text: 'Preparing images...'},
                {at: 18, text: 'Detecting text and layout...'},
                {at: 70, text: 'Reviewing detected text...'},
                {at: 88, text: 'Finalizing analysis...'},
            ]);
            
            // Reset index to 0 before wiping configurations to prevent the watcher
            // from accidentally corrupting the new fresh API data later.
            this.currentTemplateIndex = 0;
            
            this.templateConfigurations = {};

            try {
                this.setProgress(8, `Analyzing ${this.selectedTemplates.length} image(s) in parallel...`);

                const promises = this.selectedTemplates.map(async (template, index) => {
                    const imageUrl = this.getAnalysisImageUrl(template);
                    const cacheKey = this.getAnalysisCacheKey(template, imageUrl);
                    if (Object.prototype.hasOwnProperty.call(this.analysisCache, cacheKey)) {
                        return {template, data: this.analysisCache[cacheKey]};
                    }

                    const payload = {
                        image_url: imageUrl,
                        mode: 'extract'
                    };

                    try {
                        const response = await axios.post('/ocr/make-editable-v2', payload);
                        const data = response.data;

                        if (!data.success) {
                            let msg = data.message || 'Analysis failed';
                            if (msg === 'This image cannot be made editable because no text was detected.') {
                                throw new Error(`Image ${index + 1} cannot be made editable because no text was detected.`);
                            }
                            throw new Error(`Failed to analyze image ${index + 1}: ${msg}`);
                        }

                        this.cacheAnalysisResult(cacheKey, data);

                        return { template, data };
                    } catch (error) {
                        console.error(`Error analyzing image ${index + 1}:`, error);
                        if (!error.message.startsWith('Image') && !error.message.startsWith('Failed to analyze image')) {
                            error.message = `Failed to analyze image ${index + 1}: ${error.message}`;
                        }
                        throw error;
                    }
                });

                const results = await Promise.all(promises);

                this.applyAnalysisResults(results);
                if (!usingCachedAnalysis) {
                    toastr.info(`Finished analyzing ${this.selectedTemplates.length} image(s).`);
                }
            } catch (error) {
                console.error("Failed to extract text", error);
                let errorMsg = error.response?.data?.message || error.message;
                if (errorMsg.includes('cannot be made editable') || errorMsg.startsWith('Failed to analyze image')) {
                    this.errorMessage = errorMsg;
                } else {
                    this.errorMessage = 'Failed to analyze image: ' + errorMsg;
                }
            } finally {
                this.stopSimulatedProgress();
                this.isLoading = false;
            }
        },

        async handleRemoveText() {
            if (this.selectedTemplates.length === 0) return;
            if (this.translateExtractedText && this.translateTargetLanguages.length === 0) {
                this.errorMessage = 'Select at least one target language.';
                return;
            }

            this.isLoading = true;
            this.startSimulatedProgress([
                {at: 0, text: 'Preparing images...'},
                {at: 18, text: 'Removing selected text...'},
                {at: 68, text: 'Creating editable layers...'},
                {at: 88, text: 'Finalizing templates...'},
            ]);

            try {
                // Save current template context
                if (this.selectedTemplates[this.currentTemplateIndex]) {
                    const currentVal = this.selectedTemplates[this.currentTemplateIndex].value;
                    this.$set(this.templateConfigurations, currentVal, {
                        extractedTexts: this.extractedTexts,
                        selectedIndices: [...this.selectedIndices],
                        detectionImageUrl: this.detectionImageUrl,
                    });
                }

                if (!this.createNewTemplatesForEditableImages) {
                    await this.updateSelectedImagesInPlace();
                    return;
                }

                const originalIndex = this.currentTemplateIndex;
                let anyReloadNeeded = false;
                const backedUpInstances = new Set();

                this.setProgress(8, `Processing ${this.selectedTemplates.length} image(s) in parallel...`);
                
                const batchImages = [];

                this.selectedTemplates.forEach((templateObj, i) => {
                    const config = this.templateConfigurations[templateObj.value] || {};
                    const extractedTexts = config.extractedTexts || [];
                    const selectedIndices = [...new Set([
                        ...(config.selectedIndices || []),
                        ...extractedTexts.map((text, index) => text._manual ? index : null).filter(index => index !== null),
                    ])];
                    
                    let originalSrc = deleteURLParameters(templateObj.target?.getSrc(), ['lightweight', 'full_resolution']);
                    originalSrc = setURLParameters(originalSrc, { full_resolution: true });
                    const selectedImage = originalSrc;
                    
                    if (!selectedImage) return;
                    
                    const templateId = templateObj.preview?.template?.id || $("#template-group-preview").attr("data-template-id");

                    const filteredJson = selectedIndices.map(idx => {
                        const {_clientId, _manual, ...textBlock} = extractedTexts[idx];
                        return textBlock;
                    });
                    const selectedIndexSet = new Set(selectedIndices);
                    const protectedTextBlocks = extractedTexts
                        .filter((_, index) => !selectedIndexSet.has(index))
                        .map(text => {
                            const {_clientId, _manual, ...textBlock} = text;
                            return textBlock;
                        });
                    filteredJson.forEach(block => {
                        if (block.text && typeof block.text === 'string') {
                            block.content = block.text;
                        }
                    });

                    let tWidth = null;
                    let tHeight = null;
                    if (templateObj.target) {
                        tWidth = Math.round(templateObj.target.getScaledWidth());
                        tHeight = Math.round(templateObj.target.getScaledHeight());
                    }

                    batchImages.push({
                        image_url: selectedImage.toString(),
                        skip_text_extraction: true,
                        skip_automated_editing: true,
                        pre_extracted_text_blocks: filteredJson,
                        protected_text_blocks: protectedTextBlocks,
                        text_removal_method: this.textRemovalMethod,
                        enable_auto_fallback: this.enableAutoFallback,
                        template_width: tWidth,
                        template_height: tHeight,
                    });
                });
                if (batchImages.length > 0) {
                    this.setProgress(20, 'Removing text and creating editable templates...');
                    const batchPayload = {
                        images: batchImages,
                        layout_id: this.layoutId,
                        notification_source: 'layout_editor',
                        notification_name: this.selectedTemplates.length === 1
                            ? this.selectedTemplates[0].text || 'Image'
                            : `${this.selectedTemplates.length} images`,
                        translate_extracted_text: this.translateExtractedText,
                        target_languages: this.translateExtractedText ? this.translateTargetLanguages : []
                    };
                    const batchResponse = await axios.post('/networks/create-batch-layered-pdp', batchPayload);
                    if (batchResponse.data && batchResponse.data.success) {
                        if (batchResponse.data.status === 'pending') {
                            const uuid = batchResponse.data.uuid;
                            this.setProgress(70, 'Creating editable layers in the background...');
                            
                            const pollInterval = setInterval(async () => {
                                try {
                                    const statusRes = await axios.get('/networks/batch-layered-pdp-status/' + uuid);
                                    const data = statusRes.data;
                                    
                                    if (data.status === 'completed') {
                                        clearInterval(pollInterval);
                                        this.setProgress(100, 'Done!');
                                        toastr.success(`Processed successfully.`);
                                        setTimeout(() => { window.location.reload(); }, 500);
                                        this.$bvModal.hide(this.MODAL_ID);
                                    } else if (data.status === 'failed') {
                                        clearInterval(pollInterval);
                                        this.stopSimulatedProgress();
                                        this.errorMessage = 'Batch process failed: ' + data.error_message;
                                        this.isLoading = false;
                                    } else if (data.status === 'not_found') {
                                        clearInterval(pollInterval);
                                        this.stopSimulatedProgress();
                                        this.errorMessage = 'Job status could not be found.';
                                        this.isLoading = false;
                                    }
                                    // If pending, do nothing and continue polling
                                } catch (e) {
                                    console.error('Error polling status:', e);
                                    // Keep polling even on temporary network errors
                                }
                            }, 3000); // Check every 3 seconds
                            
                            // Return early to keep the modal in loading state while waiting for the job
                            return;
                        } else if (batchResponse.data.status === 'completed') {
                            this.setProgress(100, 'Done!');
                            toastr.success(`Processed successfully.`);
                            setTimeout(() => { window.location.reload(); }, 500);
                            this.$bvModal.hide(this.MODAL_ID);
                            return;
                        } else {
                            anyReloadNeeded = true;
                        }
                    } else {
                        throw new Error('Failed to add editable templates to layout');
                    }
                }

                if (anyReloadNeeded) {
                    this.setProgress(100, 'Done!');
                    toastr.success(`Processed successfully.`);
                    setTimeout(() => { window.location.reload(); }, 500);
                    this.$bvModal.hide(this.MODAL_ID);
                } else {
                    this.stopSimulatedProgress();
                    this.isLoading = false;
                    this.$bvModal.hide(this.MODAL_ID);
                }
            } catch (error) {
                console.error("Failed to remove text", error);
                this.stopSimulatedProgress();
                this.errorMessage = 'Error during final process: ' + (error.response?.data?.error_message || error.response?.data?.message || error.message);
                this.isLoading = false;
            }
        },

        getSelectedTextBlocks(templateObj) {
            const config = this.templateConfigurations[templateObj.value] || {};
            const extractedTexts = config.extractedTexts || [];
            const selectedIndices = [...new Set([
                ...(config.selectedIndices || []),
                ...extractedTexts.map((text, index) => text._manual ? index : null).filter(index => index !== null),
            ])];
            const selectedIndexSet = new Set(selectedIndices);
            const cleanBlock = (text, shouldRemovePhysically) => {
                const {_clientId, _manual, ...textBlock} = text;
                if (textBlock.text && typeof textBlock.text === 'string') {
                    textBlock.content = textBlock.text;
                }
                return {
                    ...textBlock,
                    should_remove_physically: shouldRemovePhysically,
                };
            };

            return {
                selected: selectedIndices
                    .filter(index => extractedTexts[index])
                    .map(index => cleanBlock(extractedTexts[index], true)),
                protected: extractedTexts
                    .filter((_, index) => !selectedIndexSet.has(index))
                    .map(text => cleanBlock(text, false)),
            };
        },

        captureTargetGeometry(targetElement) {
            if (!targetElement) {
                throw new Error('The selected image is no longer available on the canvas.');
            }
            if (targetElement.flipX || targetElement.flipY || targetElement.skewX || targetElement.skewY) {
                throw new Error('Updating flipped or skewed images in place is not supported.');
            }

            targetElement.setCoords();
            const {tl, tr, bl} = targetElement.aCoords || {};
            if (!tl || !tr || !bl) {
                throw new Error('Could not determine the selected image position.');
            }

            return {
                tl: {x: tl.x, y: tl.y},
                tr: {x: tr.x, y: tr.y},
                bl: {x: bl.x, y: bl.y},
            };
        },

        async updateSelectedImagesInPlace() {
            const jobs = this.selectedTemplates.map(templateObj => {
                const textBlocks = this.getSelectedTextBlocks(templateObj);
                if (textBlocks.selected.length === 0) return null;

                let imageUrl = deleteURLParameters(templateObj.target?.getSrc() || templateObj.url, ['lightweight', 'full_resolution']);
                imageUrl = setURLParameters(imageUrl, { full_resolution: true });

                return {
                    templateObj,
                    imageUrl: imageUrl.toString(),
                    selectedBlocks: textBlocks.selected,
                    protectedBlocks: textBlocks.protected,
                    geometry: this.captureTargetGeometry(templateObj.target),
                    sourceWidth: templateObj.target.width,
                    sourceHeight: templateObj.target.height,
                    shouldDeleteSourceImage: this.isTemplateFullySelected(templateObj.instanceId),
                };
            }).filter(Boolean);

            if (jobs.length === 0) {
                this.stopSimulatedProgress();
                this.isLoading = false;
                toastr.info('No text blocks were selected. No changes were made.');
                this.$bvModal.hide(this.MODAL_ID);
                return;
            }

            const preparedJobs = await Promise.all(jobs.map(async (job, index) => {
                if (job.shouldDeleteSourceImage) return job;

                this.setProgress(
                    15 + Math.round((index / jobs.length) * 35),
                    `Removing text from image ${index + 1}/${jobs.length}...`
                );
                const response = await axios.post('/ocr/make-editable-v2', {
                    image_url: job.imageUrl,
                    mode: 'remove',
                    json: [...job.selectedBlocks, ...job.protectedBlocks],
                    text_removal_method: this.textRemovalMethod,
                    enable_auto_fallback: this.enableAutoFallback,
                });
                const data = response.data || {};
                if (!data.success || data.partial_success || !data.background_image_url) {
                    throw new Error(`Failed to remove text from image ${index + 1}.`);
                }

                return {
                    ...job,
                    backgroundImageUrl: data.background_image_url,
                    sourceWidth: Number(data.source_width) || job.sourceWidth,
                    sourceHeight: Number(data.source_height) || job.sourceHeight,
                };
            }));

            for (let index = 0; index < preparedJobs.length; index++) {
                const job = preparedJobs[index];
                this.setProgress(
                    55 + Math.round((index / preparedJobs.length) * 15),
                    `Creating editable text ${index + 1}/${preparedJobs.length}...`
                );
                if (!job.shouldDeleteSourceImage) {
                    await this.replaceBackgroundImage(job.backgroundImageUrl, job.templateObj);
                }
                await this.addTextElementsToCanvas(
                    job.selectedBlocks,
                    job.templateObj,
                    job.sourceWidth,
                    job.sourceHeight,
                    job.geometry,
                );
            }

            const deletionJobs = jobs.filter(job => job.shouldDeleteSourceImage).sort((first, second) => {
                const firstTarget = first.templateObj.target;
                const secondTarget = second.templateObj.target;
                const instanceComparison = String(first.templateObj.instanceId)
                    .localeCompare(String(second.templateObj.instanceId));
                if (instanceComparison !== 0) return instanceComparison;

                const firstIsAdditional = firstTarget.id.startsWith('add_bk_theme_img_');
                const secondIsAdditional = secondTarget.id.startsWith('add_bk_theme_img_');
                if (firstIsAdditional !== secondIsAdditional) return firstIsAdditional ? -1 : 1;

                const firstIndex = Number(firstTarget.id.split('_').pop());
                const secondIndex = Number(secondTarget.id.split('_').pop());
                return secondIndex - firstIndex;
            });

            for (let index = 0; index < deletionJobs.length; index++) {
                this.setProgress(
                    70 + Math.round((index / deletionJobs.length) * 15),
                    `Removing source image ${index + 1}/${deletionJobs.length}...`
                );
                await this.removeSourceImage(deletionJobs[index].templateObj);
            }

            this.setProgress(90, 'Saving editable text and remaining image layers...');
            const {saveLayout} = getShowPageScope();
            if (!saveLayout || !(await saveLayout(false, true, 'Saving editable layers...'))) {
                throw new Error('The layout changes could not be saved.');
            }

            this.setProgress(100, 'Done!');
            toastr.success('Editable text layers created successfully.');
            this.$bvModal.hide(this.MODAL_ID);
            setTimeout(() => { window.location.reload(); }, 500);
        },

        isTemplateFullySelected(instanceId) {
            const group = this.groupedBackgroundImages.find(item => item.instanceId == instanceId);
            if (!group || group.images.length <= 1) return false;

            const selectedValues = new Set(this.selectedTemplates.map(image => image.value));
            return group.images.every(image => selectedValues.has(image.value));
        },

        async removeSourceImage(templateObj) {
            const targetElement = templateObj.target;
            if (!targetElement?.id) {
                throw new Error('The source image is no longer available on the canvas.');
            }

            if (targetElement.id.startsWith('add_bk_theme_img_')) {
                if (window.isDisplayBuilder) {
                    deleteDisplayBuilderBackgroundLayer(targetElement);
                } else {
                    deleteAdditionalBackgroundThemeImageField(targetElement);
                }
            } else if (typeof targetElement.deleteLayer === 'function') {
                await targetElement.deleteLayer();
            } else {
                throw new Error('The source image layer could not be removed.');
            }
        },

        async replaceBackgroundImage(backgroundImageUrl, templateObj) {
            const targetElement = templateObj.target;
            const instanceId = templateObj.instanceId;
            const preview = templateObj.preview;
            const {updateLayoutChanges, canvas_data, getChangesLayoutById} = getShowPageScope();
            const idParts = targetElement?.id?.split('_') || [];
            const index = parseInt(idParts[idParts.length - 1], 10);
            const isAdditionalBgImage = targetElement?.id?.startsWith('add_bk_theme_img_');
            const isRegularBgImage = targetElement?.id?.startsWith('bk_theme_img_');

            if (!targetElement || !instanceId || !preview || !updateLayoutChanges || !canvas_data
                || isNaN(index) || (!isAdditionalBgImage && !isRegularBgImage)) {
                throw new Error('The selected image field could not be identified.');
            }

            let fetchUrl = backgroundImageUrl;
            if (fetchUrl.startsWith('/')) {
                fetchUrl = window.location.origin + fetchUrl;
            }

            const response = await fetch(fetchUrl);
            if (!response.ok) {
                throw new Error(`Failed to download the processed image (${response.status}).`);
            }
            const blob = await response.blob();
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const file = new File([blob], `edited-image-${timestamp}.png`, {type: 'image/png'});
            const formData = new FormData();
            const objectUrl = URL.createObjectURL(file);
            formData.append('file', file);
            formData.append('url', objectUrl);

            let uploadResponse;
            try {
                uploadResponse = await axios.post('/banner/store_remote_image', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
            } finally {
                URL.revokeObjectURL(objectUrl);
            }

            const newImageUrl = uploadResponse.data;
            if (!newImageUrl) {
                throw new Error('Failed to upload the processed image.');
            }

            const originalProps = {
                left: targetElement.left,
                top: targetElement.top,
                angle: targetElement.angle,
                scaleX: targetElement.scaleX,
                scaleY: targetElement.scaleY,
                skewX: targetElement.skewX,
                skewY: targetElement.skewY,
                flipX: targetElement.flipX,
                flipY: targetElement.flipY,
                originX: targetElement.originX,
                originY: targetElement.originY,
            };

            await new Promise((resolve, reject) => {
                targetElement.setSrc(`/share?file=${newImageUrl}`, image => {
                    if (!image) {
                        reject(new Error('Failed to set the processed image source.'));
                        return;
                    }

                    targetElement.set(originalProps);
                    targetElement.setCoords();
                    preview.canvas.renderAll();
                    resolve();
                }, {crossOrigin: 'anonymous'});
            });

            const mergedData = {
                ...canvas_data[instanceId]?._data,
                ...(getChangesLayoutById ? getChangesLayoutById(instanceId) : {})
            };
            const updatedImageUrl = targetElement.getSrc();

            if (isAdditionalBgImage) {
                const additionalBackgroundImages = [...(mergedData.additional_background_images || [])];
                additionalBackgroundImages[index] = updatedImageUrl;
                updateLayoutChanges(instanceId, 'additional_background_images', additionalBackgroundImages);
            } else {
                const background = [...(mergedData.background || [])];
                background[index] = updatedImageUrl;
                updateLayoutChanges(instanceId, 'background', background);
            }
        },

        async addTextElementsToCanvas(textPositions, templateObj, sourceWidth, sourceHeight, geometry) {
            const targetElement = templateObj.target;
            const instanceId = templateObj.instanceId;
            const preview = templateObj.preview;

            if (!targetElement || !instanceId || !preview) return;

            const width = Number(sourceWidth);
            const height = Number(sourceHeight);
            if (!(width > 0) || !(height > 0) || !geometry?.tl || !geometry?.tr || !geometry?.bl) {
                throw new Error('Could not map the extracted text to the selected image.');
            }

            const horizontal = {
                x: (geometry.tr.x - geometry.tl.x) / width,
                y: (geometry.tr.y - geometry.tl.y) / width,
            };
            const vertical = {
                x: (geometry.bl.x - geometry.tl.x) / height,
                y: (geometry.bl.y - geometry.tl.y) / height,
            };
            const horizontalScale = Math.hypot(horizontal.x, horizontal.y);
            const verticalScale = Math.hypot(vertical.x, vertical.y);
            const imageAngle = Math.atan2(horizontal.y, horizontal.x) * (180 / Math.PI);
            const numericValue = (value, fallback) => {
                const number = Number(value);
                return Number.isFinite(number) ? number : fallback;
            };

            for (let i = 0; i < textPositions.length; i++) {
                const tp = textPositions[i];
                const content = String(tp.content || tp.text || '');
                this.setProgress(
                    60 + Math.round((i / textPositions.length) * 25),
                    `Adding text element ${i + 1}/${textPositions.length}: "${content.substring(0, 25)}..."`
                );

                const imageX = numericValue(tp.layout_pos_x, numericValue(tp.pos_x, 0));
                const imageY = numericValue(tp.layout_pos_y, numericValue(tp.pos_y, 0));
                const textWidth = numericValue(tp.layout_width, numericValue(tp.width, 200));
                const textHeight = numericValue(tp.layout_height, numericValue(tp.height, 50));
                const canvasX = geometry.tl.x + (horizontal.x * imageX) + (vertical.x * imageY);
                const canvasY = geometry.tl.y + (horizontal.y * imageX) + (vertical.y * imageY);
                const canvasWidth = Math.max(1, textWidth * horizontalScale);
                const canvasHeight = Math.max(1, textHeight * verticalScale);
                const fontSize = Math.max(1, numericValue(tp.font_size, textHeight) * verticalScale);
                const textAngle = imageAngle + numericValue(tp.rotation, numericValue(tp.angle, 0));

                const fontColor = tp.color || '#000000';
                const alignment = tp.alignment || 'left';
                const fontFamily = tp.font || 'Arial, sans-serif';

                const { text, styles } = parseText(content, fontColor);

                try {
                    await addAddField("Multi Line Text", instanceId, {
                        currentPreview: preview,
                        text: text,
                        angle: textAngle,
                        _angle: textAngle,
                        _styles: styles,
                        color: fontColor,
                        fontColor: fontColor,
                        font_color: fontColor,
                        _color: fontColor,
                        _fontColor: fontColor,
                        _font_color: fontColor,
                        font: fontFamily,
                        fontFamily: fontFamily,
                        font_family: fontFamily,
                        _font: fontFamily,
                        _fontFamily: fontFamily,
                        _font_family: fontFamily,
                        size: fontSize,
                        fontSize: fontSize,
                        fontsize: fontSize,
                        font_size: fontSize,
                        _size: fontSize,
                        _fontSize: fontSize,
                        _fontsize: fontSize,
                        _font_size: fontSize,
                        alignment: alignment,
                        textAlign: alignment,
                        text_align: alignment,
                        _alignment: alignment,
                        _textAlign: alignment,
                        _text_align: alignment,
                        offset_x: canvasX,
                        offset_y: canvasY,
                        _offset_x: canvasX,
                        _offset_y: canvasY,
                        width: canvasWidth,
                        height: canvasHeight,
                        _width: canvasWidth,
                        _height: canvasHeight,
                    });
                } catch (error) {
                    console.error(`Error adding text element ${i + 1}:`, error);
                    throw error;
                }
            }
        },
    }
}
</script>

<style scoped lang="scss">
  .step-one-content {
    min-height: 500px;
    min-height: min(500px, calc(100dvh - 12rem));
  }

  .selected-images-preview {
    height: 450px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    gap: 1rem;
    scrollbar-width: thin;
  }

  .image-thumbnail {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .image-thumbnail-placeholder {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .template-group-label {
    font-weight: 600;
    color: #495057;
  }

  .image-select-dropdown {
    width: 100%;

    ::v-deep {
      .multiselect__option {
        border-left: 4px solid transparent;
      }

      .multiselect__option--highlight,
      .multiselect__option--highlight::after {
        background-color: #f3f4f6;
        color: #35495e;
      }

      .multiselect__option--selected,
      .multiselect__option--selected::after {
        background-color: rgba(65, 184, 131, 0.14);
        color: #35495e;
      }

      .multiselect__option--selected {
        border-left-color: #41b883;
      }

      .multiselect__option--selected.multiselect__option--highlight,
      .multiselect__option--selected.multiselect__option--highlight::after {
        background: #ff6a6a !important;
        color: #fff !important;
      }

      .multiselect__option--selected.multiselect__option--highlight {
        border-left-color: #dc3545;
      }

      .btn {
        width: 100%;
        cursor: pointer;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #fff;
        border: 1px solid #ced4da;
        color: #495057;

        &:hover {
          background-color: #fff;
          border-color: #b1bbc4;
        }

        &:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }

      .dropdown-menu {
        width: 100%;
      }
    }
  }

  .text-list-container {
    background-color: white;
  }

  .text-list-scroll {
    padding: 2px 4px 8px;
  }

  .detected-text-row {
    margin: 8px 4px;

    &.is-selected .detected-text-row__header {
      border-color: #71d7a5;
      background: #f2fbf6;
    }

    &.is-hovered .detected-text-row__header {
      background: #f8fafc;
    }

    &__header {
      display: flex;
      align-items: center;
      min-height: 60px;
      padding: 12px 16px;
      border: 1px solid #d9e0e7;
      border-radius: 10px;
      background: #fff;
      cursor: pointer;
      transition: border-color 0.15s ease, background-color 0.15s ease;

      &:focus-visible {
        outline: 2px solid #238c6b;
        outline-offset: 2px;
      }

      .custom-control {
        min-height: 28px;
        padding-left: 2.35rem;
      }

      .custom-control-label {
        display: flex;
        align-items: center;
        min-height: 28px;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.3;
      }

      .custom-control-label::before,
      .custom-control-label::after {
        top: 0;
        left: -2.35rem;
        width: 28px;
        height: 28px;
        border-radius: 50%;
      }

      .custom-control-input:checked ~ .custom-control-label::before {
        border-color: #4db77a;
        background-color: #4db77a;
      }
    }

    &__toggle {
      flex: 0 0 auto;
      padding: 6px;
      border: 0;
      background: transparent;
      color: #8b99a8;
      line-height: 1;
      cursor: pointer;
    }

    &__details {
      margin: -1px 20px 12px;
      padding: 14px 18px 12px;
      border: 1px solid #a4dfc3;
      border-top: 0;
      border-radius: 0 0 8px 8px;
      background: #fff;
      color: #607089;
      font-size: 14px;
    }

    &__metadata {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 24px;
    }

    &__color {
      width: 16px;
      height: 16px;
      flex: 0 0 16px;
      padding: 0;
      overflow: hidden;
      border: 0;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;

      &::-webkit-color-swatch-wrapper {
        padding: 0;
      }

      &::-webkit-color-swatch {
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 50%;
      }
    }

    &__separator {
      width: 1px;
      height: 18px;
      flex: 0 0 1px;
      background: #dbe2ea;
    }

    &__size {
      display: inline-flex;
      align-items: center;
      gap: 3px;

      input {
        width: 34px;
        padding: 0;
        border: 0;
        border-bottom: 1px solid transparent;
        background: transparent;
        color: inherit;
        text-align: right;
        appearance: textfield;

        &:focus {
          outline: 0;
          border-bottom-color: #238c6b;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          margin: 0;
          appearance: none;
        }
      }
    }

    &__font {
      width: 100%;
      min-width: 0;
      margin-top: 10px;

      ::v-deep .multiselect,
      ::v-deep .multiselect__tags {
        min-height: 32px;
      }

      ::v-deep .multiselect__tags {
        padding-top: 5px;
        border-color: #e1e6ec;
        border-radius: 6px;
        background: #fff;
      }

      ::v-deep .multiselect__select {
        height: 32px;
      }

      ::v-deep .multiselect__single {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ::v-deep .multiselect__content-wrapper {
        right: auto;
        left: 0;
        width: 360px;
        max-width: calc(100vw - 48px);
      }

      ::v-deep .multiselect__option {
        overflow: visible;
        white-space: nowrap;
      }
    }
  }

  .active-text-row {
    .detected-text-row__header {
      box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.35);
    }
  }

  .make-editable-options {
    padding: 12px;
    border-top: 1px solid #dee2e6;
    background: #fff;
  }

  .advanced-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: #607089;
    font-weight: 600;
    cursor: pointer;
  }

  .advanced-options {
    padding-top: 10px;
  }

  .drawing-overlay {
    cursor: crosshair;
    touch-action: none;
  }

  .preview-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  @media (max-width: 767.98px) {
    .detected-text-row__details {
      margin-right: 10px;
      margin-left: 10px;
    }

    .preview-container {
      margin-top: 16px;
    }
  }

  ::v-deep .make-editable-progress-footer {
    min-height: 0;
    padding: 0;
    border-top: 0;
  }

  ::v-deep .make-editable-modal-dialog {
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
    margin-top: 1rem;
    margin-bottom: 1rem;

    .modal-content {
      max-height: calc(100vh - 2rem);
      max-height: calc(100dvh - 2rem);
      overflow: hidden;
    }
  }

  ::v-deep .make-editable-modal-body {
    min-height: 0;
    overflow-y: auto;
  }
</style>
